import React, { useState, useEffect, useCallback } from 'react';
import axios from '../config/axios';

const CATEGORY_META = {
  saving: { emoji: '🐷', color: '#10B981' },
  spending: { emoji: '🛒', color: '#F59E0B' },
  earning: { emoji: '💼', color: '#3B82F6' },
  giving: { emoji: '🎁', color: '#EC4899' },
  budgeting: { emoji: '📊', color: '#8B5CF6' },
};

const FinancialLiteracy = ({ user }) => {
  const [lessons, setLessons] = useState([]);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [quizMode, setQuizMode] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState([]);
  const [quizResult, setQuizResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const kidId = user?.user_id || user?.id;

  const fetchLessons = useCallback(async () => {
    if (!kidId) return;
    try {
      setLoading(true);
      const catParam = selectedCategory !== 'all' ? `&category=${selectedCategory}` : '';
      const res = await axios.get(`/api/v1/lessons?kid_id=${kidId}${catParam}`);
      setLessons(res.data.lessons || []);
    } catch {
      setLessons([]);
    } finally {
      setLoading(false);
    }
  }, [kidId, selectedCategory]);

  useEffect(() => { fetchLessons(); }, [fetchLessons]);

  const openLesson = async (id) => {
    try {
      const res = await axios.get(`/api/v1/lessons/${id}`);
      setSelectedLesson(res.data.lesson || res.data);
      setQuizMode(false);
      setQuizResult(null);
      setQuizAnswers([]);
    } catch { /* noop */ }
  };

  const submitQuiz = async () => {
    if (!selectedLesson) return;
    try {
      setSubmitting(true);
      const res = await axios.post('/api/v1/lessons/complete', {
        lesson_id: selectedLesson.id,
        kid_id: kidId,
        answers: quizAnswers,
      });
      setQuizResult(res.data);
    } catch {
      setQuizResult({ passed: false, score: 0, reward_granted: 0 });
    } finally {
      setSubmitting(false);
    }
  };

  const getQuiz = () => {
    if (!selectedLesson?.quiz_json) return [];
    try { return JSON.parse(selectedLesson.quiz_json); } catch { return []; }
  };

  const completedCount = lessons.filter(l => l.status === 'completed').length;
  const categories = ['all', ...Object.keys(CATEGORY_META)];

  // ==================== QUIZ RESULT ====================
  if (quizResult) {
    return (
      <div className="fl-panel">
        <div className="quiz-result">
          <span className="result-emoji">{quizResult.passed ? '🎉' : '📚'}</span>
          <h2>{quizResult.passed ? 'Great Job!' : 'Keep Learning!'}</h2>
          <p className="result-score">Score: {quizResult.score}%</p>
          {quizResult.passed && quizResult.reward_granted > 0 && (
            <div className="reward-celebration">
              <span>💰</span> You earned {quizResult.reward_granted} DadBucks!
            </div>
          )}
          {!quizResult.passed && (
            <p className="result-hint">You need 70% to pass. Watch the video and try again!</p>
          )}
          <button className="btn-primary" onClick={() => { setSelectedLesson(null); setQuizResult(null); fetchLessons(); }}>
            Back to Lessons
          </button>
        </div>
        <style>{flStyles}</style>
      </div>
    );
  }

  // ==================== QUIZ VIEW ====================
  if (quizMode && selectedLesson) {
    const quiz = getQuiz();
    const allAnswered = quizAnswers.length === quiz.length;

    return (
      <div className="fl-panel">
        <button className="back-link" onClick={() => setQuizMode(false)}>← Back to Lesson</button>
        <h3>📝 Quiz: {selectedLesson.title}</h3>
        {quiz.map((q, qi) => (
          <div key={qi} className="question-card">
            <p className="question-text">Q{qi + 1}. {q.question}</p>
            {q.options.map((opt, oi) => (
              <button
                key={oi}
                className={`option-btn ${quizAnswers[qi] === oi ? 'selected' : ''}`}
                onClick={() => { const a = [...quizAnswers]; a[qi] = oi; setQuizAnswers(a); }}
              >
                {opt}
              </button>
            ))}
          </div>
        ))}
        <button
          className={`btn-primary ${!allAnswered ? 'disabled' : ''}`}
          onClick={submitQuiz}
          disabled={!allAnswered || submitting}
        >
          {submitting ? 'Submitting...' : allAnswered ? 'Submit Answers' : `Answer all ${quiz.length} questions`}
        </button>
        <style>{flStyles}</style>
      </div>
    );
  }

  // ==================== LESSON DETAIL ====================
  if (selectedLesson) {
    const meta = CATEGORY_META[selectedLesson.category] || CATEGORY_META.saving;
    return (
      <div className="fl-panel">
        <button className="back-link" onClick={() => setSelectedLesson(null)}>← All Lessons</button>
        <div className="lesson-detail-header" style={{ background: `linear-gradient(135deg, ${meta.color}, ${meta.color}cc)` }}>
          <span className="lesson-detail-emoji">{meta.emoji}</span>
          <h2>{selectedLesson.title}</h2>
          <span className="lesson-detail-reward">Earn {selectedLesson.db_reward} DB</span>
        </div>
        <div className="lesson-content">{selectedLesson.content}</div>
        {selectedLesson.video_url && (
          <a href={selectedLesson.video_url} target="_blank" rel="noopener noreferrer" className="video-link">
            ▶️ Watch Video Lesson
          </a>
        )}
        {selectedLesson.status !== 'completed' ? (
          <button className="btn-primary" onClick={() => { setQuizMode(true); setQuizAnswers([]); }}>
            📝 Take the Quiz
          </button>
        ) : (
          <div className="completed-banner">✅ Completed — Score: {selectedLesson.quiz_score}%</div>
        )}
        <style>{flStyles}</style>
      </div>
    );
  }

  // ==================== LESSON LIST ====================
  return (
    <div className="fl-panel">
      <div className="fl-header">
        <span className="fl-header-emoji">📚</span>
        <div>
          <h3 className="fl-title">Money Skills</h3>
          <p className="fl-subtitle">{completedCount}/{lessons.length} lessons completed</p>
        </div>
      </div>

      <div className="fl-progress-bar">
        <div className="fl-progress-fill" style={{ width: lessons.length > 0 ? `${(completedCount / lessons.length) * 100}%` : '0%' }} />
      </div>

      <div className="category-filter">
        {categories.map(cat => (
          <button key={cat} className={`cat-chip ${selectedCategory === cat ? 'active' : ''}`}
            onClick={() => setSelectedCategory(cat)}>
            {cat === 'all' ? '📖 All' : `${CATEGORY_META[cat]?.emoji || ''} ${cat.charAt(0).toUpperCase() + cat.slice(1)}`}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="fl-loading"><div className="spinner" /><p>Loading lessons...</p></div>
      ) : (
        <div className="lessons-list">
          {lessons.filter(l => selectedCategory === 'all' || l.category === selectedCategory).map(lesson => {
            const meta = CATEGORY_META[lesson.category] || CATEGORY_META.saving;
            const completed = lesson.status === 'completed';
            return (
              <div key={lesson.id} className={`lesson-card ${completed ? 'completed' : ''}`} onClick={() => openLesson(lesson.id)}>
                <div className="lesson-icon" style={{ backgroundColor: meta.color + '15' }}>
                  <span>{meta.emoji}</span>
                </div>
                <div className="lesson-info">
                  <h4>{lesson.title}</h4>
                  <span className="lesson-category">{lesson.category}</span>
                </div>
                <div className="lesson-right">
                  {completed ? (
                    <span className="lesson-check">✅</span>
                  ) : (
                    <span className="lesson-reward">{lesson.db_reward} DB</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <style>{flStyles}</style>
    </div>
  );
};

const flStyles = `
  .fl-panel { background: #fff; border-radius: 16px; padding: 24px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); margin-bottom: 24px; }
  .fl-header { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
  .fl-header-emoji { font-size: 32px; background: linear-gradient(135deg, #3B82F6, #1D4ED8); padding: 8px; border-radius: 12px; }
  .fl-title { font-size: 20px; font-weight: 700; margin: 0; color: #1F2937; }
  .fl-subtitle { font-size: 12px; color: #9CA3AF; margin: 2px 0 0; }
  .fl-progress-bar { height: 6px; background: #E5E7EB; border-radius: 3px; margin-bottom: 16px; overflow: hidden; }
  .fl-progress-fill { height: 100%; background: linear-gradient(90deg, #3B82F6, #2563EB); border-radius: 3px; transition: width 0.6s ease; }
  .category-filter { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 16px; }
  .cat-chip { padding: 6px 14px; border-radius: 20px; border: none; background: #F3F4F6; color: #6B7280; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
  .cat-chip.active { background: #3B82F6; color: #fff; }
  .cat-chip:hover:not(.active) { background: #E5E7EB; }
  .fl-loading { text-align: center; padding: 40px; color: #9CA3AF; }
  .spinner { width: 32px; height: 32px; border: 3px solid #E5E7EB; border-top-color: #3B82F6; border-radius: 50%; animation: spin 0.8s linear infinite; margin: 0 auto 12px; }
  @keyframes spin { to { transform: rotate(360deg); } }
  .lessons-list { display: flex; flex-direction: column; gap: 8px; }
  .lesson-card { display: flex; align-items: center; padding: 14px; border-radius: 14px; background: #FAFAFA; border: 1px solid #F3F4F6; cursor: pointer; transition: all 0.2s; }
  .lesson-card:hover { border-color: #3B82F6; box-shadow: 0 2px 8px rgba(59,130,246,0.1); }
  .lesson-card.completed { opacity: 0.65; }
  .lesson-icon { width: 44px; height: 44px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 22px; }
  .lesson-info { flex: 1; margin-left: 12px; }
  .lesson-info h4 { margin: 0; font-size: 15px; font-weight: 600; color: #1F2937; }
  .lesson-category { font-size: 12px; color: #9CA3AF; text-transform: capitalize; }
  .lesson-right { margin-left: 8px; }
  .lesson-check { font-size: 20px; }
  .lesson-reward { background: #FEF3C7; color: #92400E; padding: 4px 10px; border-radius: 12px; font-size: 12px; font-weight: 700; }
  .back-link { background: none; border: none; color: #3B82F6; font-weight: 600; cursor: pointer; margin-bottom: 12px; font-size: 14px; padding: 0; }
  .lesson-detail-header { border-radius: 16px; padding: 32px; text-align: center; color: #fff; margin-bottom: 20px; }
  .lesson-detail-emoji { font-size: 48px; display: block; margin-bottom: 8px; }
  .lesson-detail-header h2 { margin: 0; font-size: 24px; }
  .lesson-detail-reward { display: inline-block; background: rgba(255,255,255,0.2); padding: 6px 16px; border-radius: 20px; margin-top: 12px; font-weight: 600; }
  .lesson-content { line-height: 1.7; color: #374151; margin-bottom: 20px; white-space: pre-wrap; }
  .video-link { display: block; text-align: center; background: #EF4444; color: #fff; padding: 14px; border-radius: 14px; font-weight: 700; text-decoration: none; margin-bottom: 12px; transition: background 0.2s; }
  .video-link:hover { background: #DC2626; }
  .btn-primary { display: block; width: 100%; text-align: center; background: #3B82F6; color: #fff; padding: 14px; border-radius: 14px; font-weight: 700; border: none; cursor: pointer; font-size: 16px; transition: background 0.2s; }
  .btn-primary:hover { background: #2563EB; }
  .btn-primary.disabled { background: #9CA3AF; cursor: not-allowed; }
  .completed-banner { text-align: center; background: #ECFDF5; color: #059669; padding: 14px; border-radius: 14px; font-weight: 600; }
  .question-card { background: #F9FAFB; border-radius: 14px; padding: 20px; margin-bottom: 12px; }
  .question-text { font-weight: 600; color: #1F2937; margin: 0 0 12px; }
  .option-btn { display: block; width: 100%; text-align: left; padding: 12px 14px; border-radius: 10px; border: 2px solid #E5E7EB; background: #fff; margin-bottom: 8px; cursor: pointer; transition: all 0.2s; font-size: 14px; color: #374151; }
  .option-btn.selected { border-color: #3B82F6; background: #EFF6FF; color: #1D4ED8; font-weight: 600; }
  .option-btn:hover:not(.selected) { border-color: #D1D5DB; }
  .quiz-result { text-align: center; padding: 40px 0; }
  .result-emoji { font-size: 64px; display: block; margin-bottom: 12px; }
  .result-score { font-size: 20px; color: #6B7280; }
  .reward-celebration { background: #FEF3C7; padding: 16px; border-radius: 14px; font-weight: 700; color: #92400E; margin: 16px 0; font-size: 16px; }
  .result-hint { color: #9CA3AF; margin: 12px 0; }
`;

export default FinancialLiteracy;
